import classNames from 'classnames';
import Typography from 'components/atoms/typography/typography';
import {
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  Fragment,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import './select.scss';

import Icon from 'components/atoms/icon/icon';
import './multiSelect.scss';

export interface SelectOption {
  name: string;
  selected?: boolean;
  value: string;
}
export interface SelectProps extends ComponentPropsWithoutRef<'select'> {
  bigVersion?: boolean;
  errorMessage?: string;
  label?: string;
  noFloatingLabel?: boolean;
  options?: SelectOption[];
  placeholder?: string;
  multiple?: boolean;
  selectedLabel?: string;
  defaultValue?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>((props, ref) => {
  const {
    bigVersion,
    className,
    disabled,
    errorMessage,
    label,
    placeholder,
    required = false,
    name,
    noFloatingLabel,
    onBlur,
    onChange,
    onFocus,
    options,
    multiple,
    selectedLabel,
    defaultValue,
    ...rest
  } = props;

  const [selected, setSelected] = useState<string[]>(
    () => options?.filter((option) => option.selected).map((option) => option.value) || [],
  );

  const [isLabelShown, setIsLabelShown] = useState(false);

  useEffect(() => {
    if (selected.length > 0) {
      setIsLabelShown(true);
    }
  }, [selected]);

  // For the multi select
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleSelect = (option: string) => {
    setSelected((prev) => (prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]));
    if (onChange) {
      const syntheticEvent = {
        target: { value: option },
      } as unknown as ChangeEvent<HTMLSelectElement>;
      onChange(syntheticEvent);
    }
  };

  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Fragment>
      {multiple ? (
        <div className="multi-select" ref={dropdownRef}>
          <div className={'dropdown-header'} onClick={toggleDropdown}>
            <span>
              {selected.length === 0
                ? placeholder
                : selected.length > 1
                  ? `${selected.length} ${selectedLabel}`
                  : selected[0]}
            </span>
            <span className={'arrow'}>
              {isOpen ? <Icon name={'ArrowUp'} className="icon" /> : <Icon name={'ArrowDown'} className="icon" />}
            </span>
          </div>

          {isOpen && (
            <ul className="dropdown-list">
              {options?.map((option) => (
                <li
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={selected.includes(option.value) ? 'selected' : ''}
                >
                  <input type="checkbox" checked={selected.includes(option.value)} readOnly />
                  {option.name}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div
          className={classNames('select', {
            bigVersion,
            className,
            hasError: !!errorMessage,
            isDisabled: disabled,
            isLabelShown,
            noFloatingLabel,
          })}
        >
          {label && (
            <Typography className="label" htmlFor={rest.id} tag="label" tagStyle="bodySmall">
              {label}
            </Typography>
          )}

          <select
            {...rest}
            ref={ref}
            className={classNames('select__field field', {
              select__placeholder: selected.length === 0,
            })}
            defaultValue={selected[0]}
            id={name}
            name={name}
            onBlur={onBlur}
            onChange={(event) => {
              setSelected([event.currentTarget.value]);
              setIsLabelShown(event.currentTarget.value.length > 0);
              onChange?.(event);
            }}
            onFocus={onFocus}
            required={required}
          >
            {placeholder && (
              <option className="select__placeholder" disabled selected value="">
                {placeholder}
              </option>
            )}
            {options?.map((option) => (
              <option key={option.value} className="option" value={option.value}>
                {option.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {errorMessage && (
        <Typography subjectColor="error" tag="span" tagStyle="bodyXSmall">
          *{errorMessage}
        </Typography>
      )}
    </Fragment>
  );
});

Select.displayName = 'Select';

export default Select;
